import { useEffect, useState } from "react";
import { getFile, getFileId, getFileMetadata } from "../services/fileService";
import { getEpisode, getEpisodes, getMedia, getMediaList } from "../services/mediaService";
import { Episode, Media } from "../types/media";
import { useStation } from "../contexts/StationContext";

type UseMedia = {
    media: Media;
    mediaList: Media[];
    episode: Episode;
    episodes: Episode[];
    mediaTotal: number;
    episodeTotal: number;
    isLoading: boolean;
    error: Error | null;
    setGenre: (genre: number) => void;
    setSearchQuery: (searchQuery: string) => void;
    fetchMedia: (mediaId: string) => void;
    fetchMediaList: (stationId: string) => void;
    fetchEpisode: (mediaId: string, episodeId: string) => void;
    fetchEpisodes: (mediaId: string, sortOrder?: 'asc' | 'desc', pageNumber?: number, pageSize?: number) => void;
}

export const useMedia = (mediaType: 'Audio' | 'Video', pageNumber: number = 1, pageSize: number = 10, isEditorChoice: boolean | undefined = undefined, mediaId?: string, episodeId?: string, episodesSortOrder?: 'asc' | 'desc',episodesPageNumber?: number, episodesPageSize?: number,rjId?:string): UseMedia => {
    const [mediaList, setMediaList] = useState<Media[]>([]);
    const [media, setMedia] = useState<Media>({} as Media);
    const [episode, setEpisode] = useState<Episode>({} as Episode);
    const [episodes, setEpisodes] = useState<Episode[]>([]);
    const [genre, setGenre] = useState<number>(0);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [mediaTotal, setMediaTotal] = useState<number>(0);
    const [episodeTotal, setEpisodeTotal] = useState<number>(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const { station } = useStation();

    useEffect(() => {
        if (station?.id) fetchMediaList(station?.id);
    }, [station?.id, mediaType, isEditorChoice, genre, searchQuery, pageNumber, pageSize,rjId]);

    useEffect(() => {
            if (mediaId) {
            fetchMedia(mediaId);
        }
    }, [mediaId]);
    
    useEffect(() => {
        if (mediaId && episodesSortOrder) fetchEpisodes(mediaId, episodesSortOrder, episodesPageNumber, episodesPageSize);
    }, [mediaId, episodesSortOrder, episodesPageNumber, episodesPageSize]);
    
    useEffect(() => {
        if (mediaId && episodeId) fetchEpisode(mediaId, episodeId);
    }, [mediaId, episodeId]);    

    const fetchMediaList = async (stationId: string) => {
        setIsLoading(true);
        setError(null);
        getMediaList(mediaType, stationId, isEditorChoice, genre, searchQuery, pageNumber, pageSize,rjId).then(async response => {
            const mediaWithImageData = await Promise.all(response.data.map(async (media) => {
                const filteredImages = media?.thumbnailUrls?.filter(image => image) || [];
                const images = filteredImages.map(image => getFile(image));
                
                // Fetch alt text for each image
                const imageAlts = await Promise.all(filteredImages.map(async (image) => {
                    try {
                        const fileId = getFileId(image);
                        const metadata = await getFileMetadata(fileId);
                        return metadata.altName || media.title;
                    } catch (error) {
                        console.error('Error fetching alt text:', error);
                        return media.title; // Fallback to title if alt text can't be fetched
                    }
                }));
                
                return { ...media, images, imageAlts };
            }));
            
            setMediaList(mediaWithImageData);
            setMediaTotal(response.total);
        }).catch(err => {
            const error = err instanceof Error ? err : new Error('Failed to fetch media');
            console.log('Error fetching media', error);
            setError(error);
        }).finally(() => {
            setIsLoading(false);
        });
    }

    const fetchMedia = async (id: string) => {
        setIsLoading(true);
        setError(null);
        getMedia(id).then(async response => {
            const thumbnailUrls = response?.data?.thumbnailUrls || [];
            const images = thumbnailUrls.map(image => getFile(image));
            
            // Fetch alt text for each image
            const imageAlts = await Promise.all(thumbnailUrls.map(async (image) => {
                try {
                    const fileId = getFileId(image);
                    const metadata = await getFileMetadata(fileId);
                    return metadata.altName || response.data.title;
                } catch (error) {
                    console.error('Error fetching alt text:', error);
                    return response.data.title; // Fallback to title if alt text can't be fetched
                }
            }));
            
            setMedia({ ...response.data, images, imageAlts });
        }).catch(err => {
            const error = err instanceof Error ? err : new Error('Failed to fetch media');
            console.log('Error fetching media', error);
            setError(error);
        }).finally(() => {
            setIsLoading(false);
        });
    }

    const fetchEpisodes = async (mediaId: string, sortOrder?: 'asc' | 'desc', pageNumber?: number, pageSize?: number) => {
        setIsLoading(true);
        setError(null);
        getEpisodes(mediaId, sortOrder, pageNumber, pageSize).then(response => {
            setEpisodes(response.data);
            setEpisodeTotal(response.total);
        }).catch(err => {
            const error = err instanceof Error ? err : new Error('Failed to fetch media');
            console.log('Error fetching media', error);
            setError(error);
        }).finally(() => {
            setIsLoading(false);
        });
    }

    const fetchEpisode = async (mediaId: string, episodeId: string) => {
        setIsLoading(true);
        setError(null);
        getEpisode(mediaId, episodeId).then((response: any) => {
            setEpisode(response);
        }).catch(err => {
            const error = err instanceof Error ? err : new Error('Failed to fetch media');
            console.log('Error fetching media', error);
            setError(error);
        }).finally(() => {
            setIsLoading(false);
        });
    }

    return { media, mediaList, episode, episodes, mediaTotal, episodeTotal, isLoading, error, setGenre, setSearchQuery, fetchMedia, fetchMediaList, fetchEpisode, fetchEpisodes };
}